// react
import { useContext } from 'react';

// types
import { Tag } from '../../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// mui
import { Button, Grid, Tooltip, Typography } from '@mui/material';

// context
import { ContextUserProjectPermissions } from '../../../pages/Project/Project.context';

// ui
import { Table, TableHeader, TableProps } from '../../../ui/Table/Table';
import { Time } from '../../../ui/Time/Time';

// dialogs
import { DialogDeleteTag } from '../../../dialogs/DialogDeleteTag/DialogDeleteTag';
import { DialogEditTag } from '../../../dialogs/DialogEditTag/DialogEditTag';

// icons
import {
	ModeEditOutlined as IconModeEdit,
	DeleteOutlined as IconDelete,
} from '@mui/icons-material';

// props
export interface TableBasicTagsProps extends Omit<TableProps, 'headers'> {
	data: Tag[];
	refetchTags?: () => void;
}

export const TableBasicTags = ({ data = [], refetchTags, ...props }: TableBasicTagsProps) => {
	// hooks
	const [searchParams, setSearchParams] = useSearchParams();
	const { t } = useTranslation();

	// context
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);

	// params
	const paramDialog = searchParams.get('dialog');

	const headers: TableHeader[] = [
		{
			label: t('name'),
			render: (name: string) => {
				return <Typography fontWeight={600}>{name}</Typography>;
			},
			value: 'name',
		},
		{
			label: t('created'),
			render: (created: string) => {
				return <Time date={created} />;
			},
			value: 'created',
		},
		{
			label: '',
			render: (name: string, header: TableHeader, tag: Tag) => {
				return (
					<Grid container justifyContent="flex-end" spacing={2}>
						{userProjectPermissions?.tag?.update && (
							<Grid item>
								<Tooltip arrow={true} placement="top" title={t('editTag')}>
									<span>
										<Button
											aria-label={t('editTag')}
											className="icon"
											color="primary"
											onClick={() => {
												searchParams.set('dialog', 'editTag');
												searchParams.set('dialogTag', String(tag.id));
												setSearchParams(searchParams);
											}}
											size="small"
											variant="outlined">
											<IconModeEdit />
										</Button>
									</span>
								</Tooltip>
							</Grid>
						)}
						{userProjectPermissions?.tag?.delete && (
							<Grid item>
								<Tooltip arrow={true} placement="top" title={t('deleteTag')}>
									<span>
										<Button
											aria-label={t('deleteTag')}
											className="icon"
											color="error"
											disabled={true}
											onClick={() => {
												searchParams.set('dialog', 'deleteTag');
												searchParams.set('dialogTag', String(tag.id));
												setSearchParams(searchParams);
											}}
											size="small"
											variant="outlined">
											<IconDelete />
										</Button>
									</span>
								</Tooltip>
							</Grid>
						)}
					</Grid>
				);
			},
			value: 'created',
		},
	];

	const handleCloseDialog = () => {
		searchParams.delete('dialog');
		searchParams.delete('dialogTag');
		setSearchParams(searchParams);
	};

	return (
		<>
			<Table {...props} data={data} headers={headers} />
			<DialogDeleteTag
				isOpen={paramDialog === 'deleteTag'}
				onClose={handleCloseDialog}
				onSuccess={() => {
					if (refetchTags) {
						refetchTags();
					}
				}}
			/>
			<DialogEditTag
				isOpen={paramDialog === 'editTag'}
				onClose={handleCloseDialog}
				onSuccess={() => {
					if (refetchTags) {
						refetchTags();
					}
				}}
			/>
		</>
	);
};
